import React, {useCallback, useEffect, useRef, useState} from 'react'
import {SecureDocumentKind, SecureViewerDocumentRef, SecureViewerFitBounds} from './types'
import {getDocumentTypeLabel} from '../../pm-review/utils/pmReview.formatters'

type SecureDocumentViewerModalProps<TDocument extends SecureViewerDocumentRef> = {
  document: TDocument | null
  loading: boolean
  error: string | null
  status: 'idle' | 'loading' | 'ready' | 'expired' | 'unavailable'
  documentKind: SecureDocumentKind
  src: string | null
  pdfPages: string[]
  currentPageIndex: number
  pageCount: number
  zoomPercent: number
  canZoomIn: boolean
  canZoomOut: boolean
  canGoToPreviousPage: boolean
  canGoToNextPage: boolean
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: (fitBounds?: SecureViewerFitBounds) => void
  onReset: () => void
  onRetry: () => void
  onReload: () => void
  onGoToPreviousPage: () => void
  onGoToNextPage: () => void
  onClose: () => void
  dialogLabel?: string
  subtitle?: string
  watermarkText?: string
  footerText?: string
  unsupportedMessage?: string
}

const SecureDocumentViewerModal = <TDocument extends SecureViewerDocumentRef>({
  document,
  loading,
  error,
  status,
  documentKind,
  src,
  pdfPages,
  currentPageIndex,
  pageCount,
  zoomPercent,
  canZoomIn,
  canZoomOut,
  canGoToPreviousPage,
  canGoToNextPage,
  onZoomIn,
  onZoomOut,
  onFit,
  onReset,
  onRetry,
  onReload,
  onGoToPreviousPage,
  onGoToNextPage,
  onClose,
  dialogLabel = 'Secure document viewer',
  subtitle = '', //'View-only secure preview',
  watermarkText = 'VIEW ONLY',
  footerText = '', //'Documents are displayed in a web-only viewer for assessment purposes.',
  unsupportedMessage = 'This file type is not supported for secure inline viewing in this scaffold.',
}: SecureDocumentViewerModalProps<TDocument>) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const imageViewportRef = useRef<HTMLDivElement | null>(null)
  const imageElementRef = useRef<HTMLImageElement | null>(null)
  const pdfViewportRef = useRef<HTMLDivElement | null>(null)
  const pdfPageImageRef = useRef<HTMLImageElement | null>(null)
  const autoFitKeyRef = useRef<string | null>(null)
  const [imageNaturalSize, setImageNaturalSize] = useState<{width: number; height: number} | null>(
    null
  )

  useEffect(() => {
    if (!document) {
      return
    }

    console.log('document', document)
    closeButtonRef.current?.focus()
  }, [document])

  useEffect(() => {
    autoFitKeyRef.current = null
    setImageNaturalSize(null)
  }, [document?.documentId, src])

  const zoomScale = Math.max(0.01, zoomPercent / 100)

  const handleFit = useCallback(() => {
    if (documentKind !== 'image' && documentKind !== 'pdf') {
      onFit()
      return
    }

    const viewportElement =
      documentKind === 'image' ? imageViewportRef.current : pdfViewportRef.current
    const imageElement =
      documentKind === 'image' ? imageElementRef.current : pdfPageImageRef.current

    if (!viewportElement || !imageElement) {
      onFit()
      return
    }

    const scaledLayoutWidth =
      imageElement.offsetWidth || imageNaturalSize?.width || imageElement.naturalWidth || 0
    const scaledLayoutHeight =
      imageElement.offsetHeight || imageNaturalSize?.height || imageElement.naturalHeight || 0
    const baseContentWidth =
      documentKind === 'image'
        ? imageNaturalSize?.width ||
          (zoomScale > 0 ? scaledLayoutWidth / zoomScale : scaledLayoutWidth)
        : zoomScale > 0
        ? scaledLayoutWidth / zoomScale
        : scaledLayoutWidth
    const baseContentHeight =
      documentKind === 'image'
        ? imageNaturalSize?.height ||
          (zoomScale > 0 ? scaledLayoutHeight / zoomScale : scaledLayoutHeight)
        : zoomScale > 0
        ? scaledLayoutHeight / zoomScale
        : scaledLayoutHeight

    const fitBounds: SecureViewerFitBounds = {
      viewportWidth: viewportElement.clientWidth,
      viewportHeight: viewportElement.clientHeight,
      contentWidth: baseContentWidth,
      contentHeight: baseContentHeight,
    }

    onFit(fitBounds)
  }, [documentKind, imageNaturalSize?.height, imageNaturalSize?.width, onFit, zoomScale])

  const handleAutoFit = useCallback(() => {
    const documentId = document?.documentId?.trim() || 'unknown'
    const sourceKey = src?.trim() || 'unknown'
    const autoFitKey = `${documentId}:${sourceKey}`
    if (autoFitKeyRef.current === autoFitKey) {
      return
    }

    autoFitKeyRef.current = autoFitKey
    handleFit()
  }, [document?.documentId, handleFit, src])

  const handleImageLoad = useCallback(() => {
    const imageElement = imageElementRef.current
    if (imageElement) {
      const width = imageElement.naturalWidth || imageElement.offsetWidth || 0
      const height = imageElement.naturalHeight || imageElement.offsetHeight || 0
      if (width > 0 && height > 0) {
        setImageNaturalSize((previousValue) => {
          if (previousValue && previousValue.width === width && previousValue.height === height) {
            return previousValue
          }

          return {width, height}
        })
      }
    }

    handleAutoFit()
  }, [handleAutoFit])

  const handlePdfPageLoad = useCallback(() => {
    handleAutoFit()
  }, [handleAutoFit])

  useEffect(() => {
    if (!document) {
      return
    }

    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false
      }

      const tagName = target.tagName.toLowerCase()
      return (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target.isContentEditable
      )
    }

    const handlePreviewShortcuts = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return
      }

      if (isTypingTarget(event.target)) {
        return
      }

      if (!event.ctrlKey && !event.metaKey && !event.altKey) {
        if (status === 'ready' && !loading) {
          const normalizedKey = event.key.toLowerCase()
          const isZoomInKey = event.key === '+' || event.code === 'NumpadAdd'
          const isZoomOutKey = event.key === '-' || event.code === 'NumpadSubtract'
          const isPreviousPageKey = event.key === 'ArrowLeft'
          const isNextPageKey = event.key === 'ArrowRight'

          if (isZoomInKey && canZoomIn) {
            event.preventDefault()
            event.stopPropagation()
            onZoomIn()
            return
          }

          if (isZoomOutKey && canZoomOut) {
            event.preventDefault()
            event.stopPropagation()
            onZoomOut()
            return
          }

          if (normalizedKey === 'f') {
            event.preventDefault()
            event.stopPropagation()
            handleFit()
            return
          }

          if (normalizedKey === 'r') {
            event.preventDefault()
            event.stopPropagation()
            onReset()
            return
          }

          if (documentKind === 'pdf' && isPreviousPageKey && canGoToPreviousPage) {
            event.preventDefault()
            event.stopPropagation()
            onGoToPreviousPage()
            return
          }

          if (documentKind === 'pdf' && isNextPageKey && canGoToNextPage) {
            event.preventDefault()
            event.stopPropagation()
            onGoToNextPage()
            return
          }
        }

        return
      }

      const key = event.key.toLowerCase()
      if (key === 'p' || key === 's') {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    window.addEventListener('keydown', handlePreviewShortcuts)
    return () => {
      window.removeEventListener('keydown', handlePreviewShortcuts)
    }
  }, [
    canZoomIn,
    canZoomOut,
    canGoToNextPage,
    canGoToPreviousPage,
    document,
    documentKind,
    handleFit,
    loading,
    onClose,
    onGoToNextPage,
    onGoToPreviousPage,
    onReset,
    onZoomIn,
    onZoomOut,
    status,
  ])

  useEffect(() => {
    if (documentKind !== 'image' || status !== 'ready' || loading || !src) {
      return
    }

    const imageElement = imageElementRef.current
    if (!imageElement || !imageElement.complete) {
      return
    }

    handleAutoFit()
  }, [documentKind, handleAutoFit, loading, src, status])

  if (!document) {
    return null
  }

  const currentPdfPageSrc =
    pageCount > 0 ? pdfPages[Math.max(0, Math.min(currentPageIndex, pageCount - 1))] : null
  const isExpired = status === 'expired'
  const isUnavailable = status === 'unavailable'
  const canInteractWithViewport = status === 'ready' && !loading
  const errorMessage =
    error?.trim() || (isExpired ? 'Session expired, Reload preview' : 'Preview unavailable')
  const normalizedWatermarkText = watermarkText.trim()

  return (
    <div
      className='secure-doc-overlay'
      role='dialog'
      aria-modal='true'
      aria-label={dialogLabel}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div
        className='secure-doc-modal card shadow-lg border-0'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='card-header d-flex justify-content-between align-items-center'>
          <div className='min-w-0'>
            <div className='fw-semibold text-truncate'>
              {getDocumentTypeLabel(document.type) || 'Document'}
            </div>
            <div className='small text-muted'>{subtitle}</div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={onClose}
            ref={closeButtonRef}
            aria-label='Close preview'
          >
            Close
          </button>
        </div>

        <div
          className='card-body secure-doc-body d-flex flex-column gap-2'
          onContextMenu={(event) => event.preventDefault()}
        >
          <div className='secure-doc-toolbar'>
            <div className='secure-doc-toolbar-controls'>
              <div className='btn-group' role='group' aria-label='Preview zoom controls'>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={onZoomOut}
                  disabled={!canInteractWithViewport || !canZoomOut}
                  aria-label='Zoom out'
                >
                  -
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={onZoomIn}
                  disabled={!canInteractWithViewport || !canZoomIn}
                  aria-label='Zoom in'
                >
                  +
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={handleFit}
                  disabled={!canInteractWithViewport}
                  aria-label='Fit preview'
                >
                  Fit
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={onReset}
                  disabled={!canInteractWithViewport}
                  aria-label='Reset preview'
                >
                  Reset
                </button>
              </div>

              {documentKind === 'pdf' && (
                <div className='btn-group' role='group' aria-label='Preview page controls'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={onGoToPreviousPage}
                    disabled={!canInteractWithViewport || !canGoToPreviousPage}
                    aria-label='Previous page'
                  >
                    Prev
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={onGoToNextPage}
                    disabled={!canInteractWithViewport || !canGoToNextPage}
                    aria-label='Next page'
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            <div className='small text-muted secure-doc-toolbar-status' aria-live='polite'>
              Zoom {zoomPercent}%
              {documentKind === 'pdf'
                ? ` | Page ${pageCount === 0 ? 0 : currentPageIndex + 1} of ${pageCount}`
                : ''}
            </div>
          </div>

          {!loading && isExpired && (
            <div className='p-3 bg-dark bg-opacity-50 rounded border border-secondary-subtle'>
              <div className='text-white mb-3'>{errorMessage}</div>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={onReload}
                aria-label='Reload preview'
              >
                Reload preview
              </button>
            </div>
          )}

          {!loading && isUnavailable && (
            <div className='p-3 bg-dark bg-opacity-50 rounded border border-secondary-subtle'>
              <div className='text-white mb-3'>Preview unavailable</div>
              {errorMessage !== 'Preview unavailable' ? (
                <div className='small text-muted mb-3'>{errorMessage}</div>
              ) : null}
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={onRetry}
                aria-label='Retry preview'
              >
                Retry
              </button>
            </div>
          )}

          {!loading && status === 'ready' && documentKind === 'image' && src && (
            <div className='secure-doc-viewport' ref={imageViewportRef}>
              <img
                src={src}
                alt={document.filename || 'Secure document'}
                className='secure-doc-image'
                draggable={false}
                ref={imageElementRef}
                onLoad={handleImageLoad}
                style={{
                  width: imageNaturalSize
                    ? `${Math.max(1, Math.round(imageNaturalSize.width * zoomScale))}px`
                    : undefined,
                  height: imageNaturalSize
                    ? `${Math.max(1, Math.round(imageNaturalSize.height * zoomScale))}px`
                    : undefined,
                  maxWidth: imageNaturalSize ? 'none' : undefined,
                }}
              />
            </div>
          )}

          {(status === 'ready' || loading) && documentKind === 'pdf' && currentPdfPageSrc && (
            <div className='secure-doc-viewport' ref={pdfViewportRef}>
              <div className='secure-doc-pdf-pages'>
                <img
                  key={`pdf-page-${currentPageIndex + 1}-${zoomPercent}`}
                  src={currentPdfPageSrc}
                  alt={`${document.filename || 'Secure document'} page ${currentPageIndex + 1}`}
                  className='secure-doc-image secure-doc-pdf-page'
                  draggable={false}
                  ref={pdfPageImageRef}
                  onLoad={handlePdfPageLoad}
                />
              </div>
            </div>
          )}

          {loading && documentKind === 'pdf' && !currentPdfPageSrc && (
            <div className='secure-doc-viewport'>
              <div className='secure-doc-loading-placeholder' />
            </div>
          )}

          {!loading && status === 'ready' && documentKind === 'unsupported' && (
            <div className='text-white p-3'>{unsupportedMessage}</div>
          )}

          {loading && (
            <div className='secure-doc-status-overlay' role='status' aria-live='polite'>
              Loading document...
            </div>
          )}

          {normalizedWatermarkText && (
            <div className='secure-doc-watermark' aria-hidden='true'>
              {normalizedWatermarkText}
            </div>
          )}
        </div>

        <div className='card-footer small text-muted p-1'>{footerText}</div>
      </div>
    </div>
  )
}

export default SecureDocumentViewerModal
